import {
  gprtApi,
  gprtRequestsPath,
  meetingRoomConfigPath,
  ATTACHMENT_PRE_UPLOAD_PATH,
} from "./ApiClient";
import type {
  GprtRequestListResult,
  GprtRequestDetail,
  CreateGprtRequestPayload,
  ListGprtRequestsParams,
  PreUploadFileInput,
  MeetingRoomConfig,
} from "./types";
import type {
  BackendGprtPage,
  BackendGprtRequestSummary,
  BackendGprtRequestDetail,
  BackendAttachmentPreUploadResponse,
  BackendMeetingRoomConfig,
} from "./backendTypes";
import {
  adaptGprtListResponse,
  adaptGprtDetail,
  adaptCreateGprtPayload,
  adaptGprtListParams,
  adaptMeetingRoomConfig,
} from "./adapters";
import { logError } from "@/utils/errorHandler";

const DEFAULT_PAGE_SIZE = 20;

// ──────────────────────────────────────────────
// Create
// ──────────────────────────────────────────────

/** 201 Created with an empty body — nothing meaningful to return. */
export async function createGprtRequest(
  propertyId: string,
  payload: CreateGprtRequestPayload
): Promise<void> {
  const body = adaptCreateGprtPayload(payload, propertyId);
  await gprtApi.post(`${gprtRequestsPath(propertyId)}`, body);
}

/**
 * Client-side pre-check for the duplicate-request landmine (contract Known Issue #1):
 * the backend's intended 409 on a duplicate (propertyId, platformId, platformRfpId)
 * actually surfaces as a generic 500 with no useful body, so the frontend can't tell
 * "duplicate" apart from "server broke" after the fact. Check beforehand instead.
 */
export async function findDuplicateGprtRequest(
  propertyId: string,
  platformId: number,
  platformRfpId: string
): Promise<boolean> {
  const normalizedRfpId = platformRfpId.trim().toLowerCase();
  if (!normalizedRfpId) return false;
  // No server-side filter for platformId/platformRfpId exists — pull a generously
  // sized page and check client-side. Not exhaustive against a very large backlog,
  // but covers the realistic case this check exists for.
  const { content } = await listGprtRequests(propertyId, { size: 200 });
  return content.some(
    (r) =>
      r.platformId === platformId &&
      (r.platformRfpId ?? "").trim().toLowerCase() === normalizedRfpId
  );
}

// ──────────────────────────────────────────────
// List
// ──────────────────────────────────────────────

export async function listGprtRequests(
  propertyId: string,
  params?: ListGprtRequestsParams
): Promise<GprtRequestListResult> {
  const size = params?.size ?? DEFAULT_PAGE_SIZE;
  const response = await gprtApi.get<
    BackendGprtPage<BackendGprtRequestSummary>
  >(gprtRequestsPath(propertyId), {
    params: adaptGprtListParams({ ...params, size }),
  });
  return adaptGprtListResponse(response.data, size);
}

/**
 * No multi-property GPRT list endpoint exists yet (unlike GPC's /pricing/requests) —
 * fan out one request per property and merge client-side. Isolated here so swapping
 * this for a real server-side endpoint later is a one-function change.
 */
export async function listGprtRequestsAcrossProperties(
  propertyIds: string[],
  params?: ListGprtRequestsParams
): Promise<GprtRequestListResult> {
  if (propertyIds.length === 0) {
    throw new Error(
      "listGprtRequestsAcrossProperties requires at least one propertyId"
    );
  }
  const results = await Promise.allSettled(
    propertyIds.map((id) => listGprtRequests(id, params))
  );
  results.forEach((r, i) => {
    if (r.status === "rejected") {
      logError(
        r.reason,
        `GPRT: Failed to list requests for property ${propertyIds[i]}`
      );
    }
  });
  const content = results
    .filter(
      (r): r is PromiseFulfilledResult<GprtRequestListResult> =>
        r.status === "fulfilled"
    )
    .flatMap((r) => r.value.content)
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  const hasMore = results.some(
    (r) => r.status === "fulfilled" && r.value.hasMore
  );
  const failedCount = results.filter((r) => r.status === "rejected").length;
  return { content, hasMore, failedCount };
}

// ──────────────────────────────────────────────
// Detail + Acknowledge
// ──────────────────────────────────────────────

export async function getGprtRequestDetail(
  propertyId: string,
  id: string
): Promise<GprtRequestDetail> {
  const response = await gprtApi.get<BackendGprtRequestDetail>(
    `${gprtRequestsPath(propertyId)}/${id}`
  );
  if (!response.data)
    throw new Error("Empty response from server for GPRT request detail");
  return adaptGprtDetail(response.data);
}

/** 204 No Content, even for an unrecognized id — the response gives no reliable
 * signal that anything was actually acknowledged (contract Known Issue #3). */
export async function acknowledgeGprtRequest(
  propertyId: string,
  id: string
): Promise<void> {
  await gprtApi.post(`${gprtRequestsPath(propertyId)}/${id}/acknowledge`);
}

// ──────────────────────────────────────────────
// Meeting Room Config
// ──────────────────────────────────────────────

/** `propertyId` is accepted but unused server-side — the config is a static,
 * global constant list, not property-specific. Pass any property on hand. */
export async function getMeetingRoomConfig(
  propertyId: string
): Promise<MeetingRoomConfig> {
  const response = await gprtApi.get<BackendMeetingRoomConfig>(
    meetingRoomConfigPath(propertyId)
  );
  return adaptMeetingRoomConfig(response.data);
}

// ──────────────────────────────────────────────
// Attachments
// ──────────────────────────────────────────────

export async function preUploadAttachments(
  files: PreUploadFileInput[]
): Promise<string[]> {
  const response = await gprtApi.post<BackendAttachmentPreUploadResponse>(
    ATTACHMENT_PRE_UPLOAD_PATH,
    {
      files: files.map((f) => ({
        file_name: f.fileName,
        content: f.content,
        content_type: f.contentType,
      })),
    }
  );
  return response.data?.attachment_ids ?? [];
}
